import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  ContentChild,
  OnChanges,
  SimpleChanges
} from '@angular/core';

@Component({
  selector: 'tree-root',
  templateUrl: './tree-root.component.html',
  styleUrls: ['./tree-root.component.scss'],
  standalone: false
})
export class TreeRootComponent implements OnChanges {
  @Input() id: string;
  @Input() nodes: any[] = [];
  @Input() focused: boolean;
  @Input() options: any;
  /**
   * Optional: nodes for which this returns true are not rendered. They stay in
   * `nodes` untouched, so a caller that serializes its own data keeps them.
   * Receives the original node data.
   */
  @Input() hideNode?: (data: any) => boolean;
  @Output() event: EventEmitter<any> = new EventEmitter<any>();
  // compatibility output used by legacy code expecting (moveNode)
  @Output() moveNode: EventEmitter<any> = new EventEmitter<any>();

  @ContentChild(TemplateRef, { static: true }) nodeTemplate: TemplateRef<any>;

  wrapperNodes: any[] = [];

  treeModel: any = {
    expandedNodeIds: {},
    filterNodes: (predicate: any, autoExpand: boolean = true) => {
      try {
        if (typeof predicate === 'function') {
          const walk = (nodes: any[]) => {
            nodes.forEach(n => {
              // keep node if predicate returns true
              try {
                n.__visible = !!predicate(n);
              } catch (e) {
                n.__visible = true;
              }
              if (n.nodes && n.nodes.length) {
                walk(n.nodes);
                // if any child visible, ensure parent is visible
                if (n.nodes.some((c: any) => c.__visible)) {
                  n.__visible = true;
                }
              }
            });
          };
          walk(this.wrapperNodes);
        } else if (typeof predicate === 'string') {
          // simple text search: show nodes that include the text in their data.title
          const txt = (predicate || '').toString().toUpperCase();
          const walk = (nodes: any[]) => {
            nodes.forEach(n => {
              const title =
                n.data && n.data.title
                  ? n.data.title.toString().toUpperCase()
                  : '';
              n.__visible = title.indexOf(txt) !== -1;
              if (n.nodes && n.nodes.length) {
                walk(n.nodes);
                if (n.nodes.some((c: any) => c.__visible)) {
                  n.__visible = true;
                }
              }
            });
          };
          walk(this.wrapperNodes);
        }
      } catch (e) {
        // fail safe: make everything visible
        const walkAll = (nodes: any[]) =>
          nodes.forEach(n => {
            n.__visible = true;
            if (n.nodes) walkAll(n.nodes);
          });
        walkAll(this.wrapperNodes);
      }
    },
    clearFilter: () => {
      const walk = (nodes: any[]) =>
        nodes.forEach(n => {
          delete n.__visible;
          if (n.nodes) walk(n.nodes);
        });
      walk(this.wrapperNodes);
    },
    update: () => {
      // no-op for compatibility; change detection will update template
      return;
    }
  };

  /** Whether a mapped node should be rendered — see `hideNode`. */
  isShown(node: any): boolean {
    return !this.hideNode || !this.hideNode(node?.data);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['nodes']) {
      this.wrapperNodes = (this.nodes || []).map(n => this.mapNode(n, null));
    }
    if (changes['focused']) {
      // map focused boolean to wrapperNodes' isFocused flag
      const setFocus = (nodes: any[]) => {
        nodes.forEach(n => {
          n.isFocused = !!this.focused;
          if (n.nodes) setFocus(n.nodes);
        });
      };
      setFocus(this.wrapperNodes);
    }
  }

  private mapNode(orig: any, parent: any) {
    const self = this;

    // Calculate level/depth for compatibility
    let level = 0;
    let p = parent;
    while (p) {
      level++;
      p = p.parent;
    }

    const node: any = {
      data: orig,
      id: orig.id,
      level: level,
      isFolder: !!orig.isFolder,
      isActive: !!orig.isActive,
      isFocused: !!orig.isFocused,
      isExpanded: !!orig.expanded,
      parent: parent,
      // methods expected by the template
      getNodePadding: function () {
        return this.level * 16 + 'px';
      },
      allowDrop: () => true,
      allowDrag: () => true,
      mouseAction: function (action: string, ev?: any) {
        // map basic actions to event names similar to circlon
        let evName = action;
        if (action === 'click') evName = 'activate';
        if (action === 'contextMenu') evName = 'contextMenu';
        if (action === 'dblClick') evName = 'dblClick';
        const out = { eventName: evName, node: this, originalEvent: ev };
        self.event.emit(out);

        // Also emit focus event for click
        if (action === 'click') {
          self.event.emit({
            eventName: 'focus',
            node: this,
            originalEvent: ev
          });
        }
      },
      onDrop: function ($event: any) {
        const out = { eventName: 'drop', node: this, originalEvent: $event };
        self.event.emit(out);
        try {
          self.moveNode.emit(out);
        } catch (e) {
          // ignore
        }
      },
      toggleExpand: function () {
        this.isExpanded = !this.isExpanded;
        const out = {
          eventName: this.isExpanded ? 'expand' : 'collapse',
          node: this
        };
        self.event.emit(out);
      }
    };

    const childData = orig.nodes || orig.children;
    const mappedChildren = Array.isArray(childData)
      ? childData.map((c: any) => this.mapNode(c, node))
      : [];

    node.nodes = mappedChildren;
    node.children = mappedChildren;
    node.hasChildren = mappedChildren.length > 0;

    return node;
  }
}
